import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { getApiBaseUrl, safeParseJson } from '../utils/apiBaseUrl';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area
} from 'recharts';
import { 
  Moon, Sun, Bell, ChevronDown, ChevronUp, Filter, 
  TrendingUp, TrendingDown, Users, Activity, Zap, Target, 
  BarChart3, PieChart as PieChartIcon, Globe, Database, 
  Cpu, Brain, Sparkles, Rocket, Star, Award, Shield,
  Eye, Clock, MessageCircle, UserCheck, ArrowUpRight, ArrowDownRight, Calendar, Download,
  Home, Settings, User, LogOut, RefreshCw
} from 'lucide-react';
import { RecentActivitiesAnalytics } from '../components/Analytics';

// No more mock data - only real database data is used

// Helper function to get day suffix (1st, 2nd, 3rd, etc.)
const getDaySuffix = (day) => {
  if (day >= 11 && day <= 13) {
    return 'th';
  }
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';  
    case 3: return 'rd';
    default: return 'th';
  }
};

// Calendar utility functions
const getDaysInMonth = (year, month) => {
  return new Date(year, month + 1, 0).getDate();
};

const getFirstDayOfMonth = (year, month) => {
  return new Date(year, month, 1).getDay();
};

const getMonthName = (month) => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return months[month];
};

const formatDate = (year, month, day) => {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

// DatePicker component
const DatePicker = ({ selectedDate, onDateSelect, onClose }) => {
  const now = new Date();
  const [currentMonth, setCurrentMonth] = useState(now.getMonth());
  const [currentYear, setCurrentYear] = useState(now.getFullYear());
  
  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
  const today = new Date();
  
  const days = [];
  
  // Previous month's days
  const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
  const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
  const daysInPrevMonth = getDaysInMonth(prevYear, prevMonth);
  
  for (let i = firstDay - 1; i >= 0; i--) {
    days.push({
      day: daysInPrevMonth - i,
      month: prevMonth,
      year: prevYear,
      isCurrentMonth: false,
      isPrevMonth: true
    });
  }
  
  // Current month's days
  for (let day = 1; day <= daysInMonth; day++) {
    days.push({
      day,
      month: currentMonth,
      year: currentYear,
      isCurrentMonth: true,
      isPrevMonth: false
    });
  }
  
  // Next month's days
  const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
  const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
  const remainingDays = 42 - days.length;
  
  for (let day = 1; day <= remainingDays; day++) {
    days.push({
      day,
      month: nextMonth,
      year: nextYear,
      isCurrentMonth: false,
      isPrevMonth: false
    });
  }

  const isSelected = (dayInfo) => {
    const dateStr = formatDate(dayInfo.year, dayInfo.month, dayInfo.day);
    return selectedDate === dateStr;
  };

  const isToday = (dayInfo) => {
    return dayInfo.day === today.getDate() && 
           dayInfo.month === today.getMonth() && 
           dayInfo.year === today.getFullYear() &&
           dayInfo.isCurrentMonth;
  };

  return (
    <div style={{
      position: 'absolute',
      top: '100%',
      left: 0,
      zIndex: 10000,
      marginTop: '8px',
      background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.95) 100%)',
      border: '1px solid rgba(59, 130, 246, 0.3)',
      borderRadius: '16px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 32px rgba(59, 130, 246, 0.1)',
      backdropFilter: 'blur(24px)',
      padding: '20px',
      width: '300px',
      animation: 'slideInDown 0.3s ease'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px'
      }}>
        <button
          onClick={() => {
            if (currentMonth === 0) {
              setCurrentMonth(11);
              setCurrentYear(currentYear - 1);
            } else {
              setCurrentMonth(currentMonth - 1);
            }
          }}
          style={{
            background: 'rgba(59, 130, 246, 0.2)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '8px',
            color: 'white',
            padding: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(59, 130, 246, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(59, 130, 246, 0.2)';
          }}
        >
          ←
        </button>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{
            color: 'white',
            fontSize: '16px',
            fontWeight: '600'
          }}>
            {getMonthName(currentMonth)} {currentYear}
          </span>
        </div>
        
        <button
          onClick={() => {
            if (currentMonth === 11) {
              setCurrentMonth(0);
              setCurrentYear(currentYear + 1);
            } else {
              setCurrentMonth(currentMonth + 1);
            }
          }}
          style={{
            background: 'rgba(59, 130, 246, 0.2)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '8px',
            color: 'white',
            padding: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(59, 130, 246, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(59, 130, 246, 0.2)';
          }}
        >
          →
        </button>
      </div>

      {/* Days of week */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '4px',
        marginBottom: '8px'
      }}>
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
          <div key={day} style={{
            textAlign: 'center',
            padding: '8px 4px',
            fontSize: '12px',
            fontWeight: '600',
            color: 'rgba(255, 255, 255, 0.6)'
          }}>
            {day}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '4px'
      }}>
        {days.slice(0, 42).map((dayInfo, index) => (
          <button
            key={index}
            onClick={() => {
              const dateStr = formatDate(dayInfo.year, dayInfo.month, dayInfo.day);
              onDateSelect(dateStr);
              onClose();
            }}
            style={{
              padding: '8px 4px',
              background: isSelected(dayInfo) 
                ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'
                : isToday(dayInfo)
                ? 'rgba(59, 130, 246, 0.3)'
                : dayInfo.isCurrentMonth 
                ? 'transparent' 
                : 'transparent',
              border: isToday(dayInfo) ? '1px solid rgba(59, 130, 246, 0.5)' : 'none',
              borderRadius: '8px',
              color: isSelected(dayInfo)
                ? 'white'
                : dayInfo.isCurrentMonth 
                ? 'white' 
                : 'rgba(255, 255, 255, 0.4)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: isSelected(dayInfo) || isToday(dayInfo) ? '600' : '500',
              transition: 'all 0.2s ease',
              minHeight: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseEnter={(e) => {
              if (!isSelected(dayInfo)) {
                e.target.style.background = 'rgba(59, 130, 246, 0.2)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSelected(dayInfo)) {
                e.target.style.background = isToday(dayInfo) 
                  ? 'rgba(59, 130, 246, 0.3)'
                  : 'transparent';
              }
            }}
          >
            {dayInfo.day}
          </button>
        ))}
      </div>
    </div>
  );
};

// Mock data removed - using real database data only

function AnalyticsPage2025Fixed() {
  const { theme, currentThemeName, toggleTheme } = useTheme();
  const { currentLanguage, t } = useLanguage();
  const { user: authUser, logout } = useAuth();
  const navigate = useNavigate();
  const todayLabel = new Date().toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  const [selectedMetric, setSelectedMetric] = useState('chats');
  const [timeRange, setTimeRange] = useState('7d');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showDateRangeDropdown, setShowDateRangeDropdown] = useState(false);
  const dateRangeDropdownRef = useRef(null);
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);
  const startCalendarRef = useRef(null);
  const endCalendarRef = useRef(null);
  const [analyticsData, setAnalyticsData] = useState({
    userStats: {
      totalUsers: 0,
      activeUsers: 0,
      newUsersThisWeek: 0,
      totalChats: 0,
      avgSessionDuration: '0 min',
      userSatisfaction: 0
    },
    businessTypeData: [],
    popularQuestions: [],
    dailyActivity: [],
    questionCategories: [],
    recentActivity: [],
    userGrowthData: []
  });

  // Fetch user growth data separately
  const fetchUserGrowthData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Build user growth API URL
      let growthUrl = `${getApiBaseUrl()}/api/analytics/user-growth?timeRange=${timeRange}&_t=${Date.now()}`;
      if (timeRange === 'custom' && customDateRange.startDate && customDateRange.endDate) {
        growthUrl += `&startDate=${customDateRange.startDate}&endDate=${customDateRange.endDate}`;
      }
      
      const growthResponse = await fetch(growthUrl, {
        headers,
        credentials: 'include'
      });
      
      if (growthResponse.ok) {
        const growthData = await safeParseJson(growthResponse);
        console.log('✅ User growth data loaded:', growthData);
        
        // Convert backend data format to chart format
        let formattedData = [];
        if (growthData && growthData.userGrowth && growthData.userGrowth.length > 0) {
          formattedData = growthData.userGrowth.map(item => ({
            date: item.date,
            users: item.users || 0,
            chats: item.sessions || 0,
            messages: item.sessions * 2 || 0, // Estimate messages based on sessions
            engagement: item.engagement || 0
          }));
        } else {
          // No fallback data - show empty chart when no API data
          formattedData = [];
        }
        
        setAnalyticsData(prevData => ({
          ...prevData,
          userGrowthData: formattedData
        }));
      } else {
        throw new Error(`User growth API failed with status: ${growthResponse.status}`);
      }
    } catch (error) {
      console.warn('⚠️ Failed to fetch user growth data, showing empty chart:', error);
      // No fallback data - show empty chart when API fails
      setAnalyticsData(prevData => ({
        ...prevData,
        userGrowthData: []
      }));
    }
  }, [timeRange, customDateRange]);

  // No fallback data - API failures will show empty charts

  // Fetch analytics data based on date range
  const fetchAnalyticsData = useCallback(async () => {
    try {
      setRefreshing(true);
      
      // Build API URL with date range parameters
      let url = `${getApiBaseUrl()}/api/analytics/comprehensive?timeRange=${timeRange}&_t=${Date.now()}`;
      if (timeRange === 'custom' && customDateRange.startDate && customDateRange.endDate) {
        url += `&startDate=${customDateRange.startDate}&endDate=${customDateRange.endDate}`;
      }
      
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Fetch comprehensive analytics data
      const response = await fetch(url, {
        headers,
        credentials: 'include'
      });

      if (response.ok) {
        const data = await safeParseJson(response);
        if (!data || typeof data !== 'object') {
          setAnalyticsData(prev => ({
            ...prev,
            userStats: {
              totalUsers: 0,
              activeUsers: 0,
              newUsersThisWeek: 0,
              totalChats: 0,
              avgSessionDuration: '0 min',
              userSatisfaction: 0
            },
            businessTypeData: [],
            popularQuestions: [],
            dailyActivity: [],
            questionCategories: [],
            recentActivity: [],
            userGrowthData: prev.userGrowthData || []
          }));
          return;
        }
        // Fetch recent activities separately
        let recentActivities = [];
        try {
          const timeRangeMap = {
            '7d': '7d',
            '30d': '7d', // Use 7 days for recent activities even if main data is 30 days
            '90d': '7d'  // Use 7 days for recent activities even if main data is 90 days
          };
          
          const activitiesUrl = `${getApiBaseUrl()}/api/analytics/recent-activities?timeRange=${timeRangeMap[timeRange] || '7d'}&limit=20&_t=${Date.now()}`;
          const activitiesResponse = await fetch(activitiesUrl, {
            headers,
            credentials: 'include'
          });
          
          if (activitiesResponse.ok) {
            const activitiesData = await safeParseJson(activitiesResponse);
            recentActivities = (activitiesData && activitiesData.activities) ? activitiesData.activities : [];
            console.log('✅ Recent activities loaded:', recentActivities);
          }
        } catch (activitiesError) {
          console.warn('⚠️ Failed to fetch recent activities:', activitiesError);
        }
        
        setAnalyticsData({
          ...data,
          recentActivity: recentActivities
        });
        console.log('✅ Real analytics data loaded:', data);
        
        fetchUserGrowthData();
      } else {
        throw new Error(`API request failed with status: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Failed to fetch analytics data:', error);
      setAnalyticsData(prevData => ({
        ...prevData,
        userStats: {
          totalUsers: 0,
          activeUsers: 0,
          newUsersThisWeek: 0,
          totalChats: 0,
          avgSessionDuration: '0 min',
          userSatisfaction: 0
        },
        businessTypeData: [],
        popularQuestions: [],
        dailyActivity: [],
        questionCategories: [],
        recentActivity: [],
        userGrowthData: []
      }));
    } finally {
      setRefreshing(false);
    }
  }, [timeRange, customDateRange, fetchUserGrowthData]);

  // Force dark mode for analytics
  useEffect(() => {
    if (currentThemeName !== 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark');
    }
    return () => {
      if (currentThemeName !== 'dark') {
        document.documentElement.setAttribute('data-theme', currentThemeName);
      }
    };
  }, [currentThemeName]);

  // Fetch data when component mounts or date range changes
  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  // Close date picker when pressing Escape key
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && showDatePicker) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener('keydown', handleEscapeKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [showDatePicker]);

  // Close date range dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateRangeDropdownRef.current && !dateRangeDropdownRef.current.contains(event.target)) {
        setShowDateRangeDropdown(false);
      }
    };

    if (showDateRangeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDateRangeDropdown]);

  // Close calendar popups when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (startCalendarRef.current && !startCalendarRef.current.contains(event.target)) {
        setShowStartCalendar(false);
      }
      if (endCalendarRef.current && !endCalendarRef.current.contains(event.target)) {
        setShowEndCalendar(false);
      }
    };

    if (showStartCalendar || showEndCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStartCalendar, showEndCalendar]);

  // Date range utility functions
  const formatShortDate = (date) =>
    date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const getPresetRangeDates = (range) => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const days = range === '30d' ? 29 : range === '90d' ? 89 : 6;
    start.setDate(start.getDate() - days);
    return { start, end };
  };

  const getDisplayText = () => {
    switch (timeRange) {
      case '7d': {
        const { start, end } = getPresetRangeDates('7d');
        return `Last 7 days (${formatShortDate(start)} – ${formatShortDate(end)})`;
      }
      case '30d': {
        const { start, end } = getPresetRangeDates('30d');
        return `Last 30 days (${formatShortDate(start)} – ${formatShortDate(end)})`;
      }
      case '90d': {
        const { start, end } = getPresetRangeDates('90d');
        return `Last 90 days (${formatShortDate(start)} – ${formatShortDate(end)})`;
      }
      case 'custom':
        if (customDateRange.startDate && customDateRange.endDate) {
          const start = new Date(customDateRange.startDate).toLocaleDateString();
          const end = new Date(customDateRange.endDate).toLocaleDateString();
          return `${start} - ${end}`;
        }
        return 'Custom range';
      default: {
        const { start, end } = getPresetRangeDates('7d');
        return `Last 7 days (${formatShortDate(start)} – ${formatShortDate(end)})`;
      }
    }
  };

  const getChartGroupingLabel = () => {
    if (timeRange === '7d') {
      return 'Daily view';
    } else if (timeRange === '30d') {
      return 'Weekly view';
    } else if (timeRange === '90d') {
      return 'Monthly view';
    } else if (timeRange === 'custom' && customDateRange.startDate && customDateRange.endDate) {
      const startDate = new Date(customDateRange.startDate);
      const endDate = new Date(customDateRange.endDate);
      const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
      
      if (daysDiff <= 7) {
        return 'Daily view';
      } else if (daysDiff <= 60) {
        return 'Weekly view';
      } else {
        return 'Monthly view';
      }
    }
    return 'Daily view';
  };

  const handleDateRangeChange = (type, value) => {
    setCustomDateRange(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const applyCustomDateRange = () => {
    if (customDateRange.startDate && customDateRange.endDate) {
      setTimeRange('custom');
      setShowDatePicker(false);
      console.log(`✅ Applying custom date range: ${customDateRange.startDate} to ${customDateRange.endDate}`);
      // Trigger data fetch for custom range - this was missing!
      // The useEffect will trigger automatically when timeRange changes to 'custom'
    }
  };

  const resetDateRange = () => {
    setCustomDateRange({ startDate: '', endDate: '' });
    setTimeRange('7d');
    setShowDatePicker(false);
  };

  // Dropdown functions
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleProfileClick = () => {
    navigate('/profile');
    setShowDropdown(false);
  };

  const handleUserListClick = () => {
    navigate('/userlist');
    setShowDropdown(false);
  };

  const handleHomeClick = () => {
    navigate('/fullchat');
    setShowDropdown(false);
  };

  const handleAdminClick = () => {
    navigate('/admin');
    setShowDropdown(false);
  };

  const handleSettingsClick = () => {
    navigate('/settings');
    setShowDropdown(false);
  };

  // Custom Tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Find the data point to get the full date
      const dataPoint = analyticsData.dailyActivity.find(item => item.date === label);
      const fullDate = dataPoint?.fullDate ? new Date(dataPoint.fullDate).toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }) : label;

      return (
        <div style={{
          background: 'rgba(0, 0, 0, 0.95)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '12px',
          padding: '12px 16px',
          color: 'white',
          fontSize: '14px',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)',
          zIndex: 1000,
          maxWidth: '250px'
        }}>
          <div style={{ fontWeight: '600', marginBottom: '8px', fontSize: '12px', opacity: 0.8 }}>
            {fullDate}
          </div>
          <div style={{ fontWeight: '600', marginBottom: '8px' }}>
            {label}
          </div>
          {payload.map((entry, index) => (
            <div key={index} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '4px'
            }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: entry.color
              }} />
              <span>{entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}{entry.dataKey === 'engagement' ? '%' : ''}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  // Calculate stats from CHART data (userGrowthData), not dailyActivity!
  const chartData = analyticsData.userGrowthData || [];
  const currentData = chartData[chartData.length - 1];
  const previousData = chartData[chartData.length - 2];
  const currentValue = currentData?.[selectedMetric] || 0;
  const previousValue = previousData?.[selectedMetric] || 0;
  const change = previousValue > 0 ? ((currentValue - previousValue) / previousValue * 100) : 0;
  const average = chartData.length > 0 ? 
    Math.round(chartData.reduce((sum, item) => sum + (item[selectedMetric] || 0), 0) / chartData.length) : 0;

  return (
    <>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes fadeInScale {
            from { 
              opacity: 0; 
              transform: translate(-50%, -50%) scale(0.9); 
            }
            to { 
              opacity: 1; 
              transform: translate(-50%, -50%) scale(1); 
            }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideInDown {
            from { 
              opacity: 0; 
              transform: translateY(-10px);
            }
            to { 
              opacity: 1; 
              transform: translateY(0);
            }
          }
          @keyframes slideInDownBounce {
            0% { 
              opacity: 0; 
              transform: translateY(-15px) scale(0.95);
            }
            60% { 
              opacity: 0.8; 
              transform: translateY(2px) scale(1.02);
            }
            100% { 
              opacity: 1; 
              transform: translateY(0) scale(1);
            }
          }
        `}
      </style>
      <div style={{
        backgroundColor: '#0A0A0A',
        color: 'white',
        minHeight: '100vh',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
        position: 'relative'
      }}>
      {/* Header */}
      <header style={{ 
        background: 'rgba(15, 23, 42, 0.95)',
        borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
        backdropFilter: 'blur(20px)',
        padding: '20px 24px',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          <div
            onClick={() => navigate('/fullchat')}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/fullchat');
              }
            }}
            title="Go to main page"
            style={{ display: 'flex', alignItems: 'center', gap: '24px', cursor: 'pointer' }}
          >
            <div style={{
              width: '44px',
              height: '44px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Brain size={24} color="white" />
            </div>
            <div>
              <div style={{ 
                fontSize: '26px', 
                fontWeight: '700',
                background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                Oakland AI Analytics
              </div>
              <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)' }}>
                Updated {todayLabel}
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Navigation Dropdown */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  background: showDropdown 
                    ? 'rgba(59, 130, 246, 0.2)'
                    : 'rgba(30, 41, 59, 0.8)',
                  border: `1px solid ${showDropdown 
                    ? 'rgba(59, 130, 246, 0.3)'
                    : 'rgba(59, 130, 246, 0.2)'
                  }`,
                  borderRadius: 12,
                  color: '#93c5fd',
                  cursor: 'pointer',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.2s ease',
                  fontWeight: 600,
                  fontSize: 14
                }}
                onMouseEnter={(e) => {
                  if (!showDropdown) {
                    e.target.style.background = 'rgba(59, 130, 246, 0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!showDropdown) {
                    e.target.style.background = 'rgba(30, 41, 59, 0.8)';
                  }
                }}
              >
                <BarChart3 size={16} />
                <span>Navigation</span>
                {showDropdown ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>

              {/* Dropdown Menu */}
              {showDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '12px',
                  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.95) 100%)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: 16,
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 32px rgba(59, 130, 246, 0.1)',
                  backdropFilter: 'blur(24px)',
                  minWidth: '240px',
                  zIndex: 9999,
                  animation: 'slideInDownBounce 0.3s ease',
                  overflow: 'hidden',
                  transform: 'translateZ(0)', // Hardware acceleration
                  willChange: 'transform, opacity'
                }}>
                  {/* User Info Section */}
                  <div style={{
                    padding: '16px',
                    borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
                    background: 'rgba(30, 41, 59, 0.5)'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #22c55e 100%)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '16px',
                        fontWeight: '600',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                      }}>
                        {authUser?.name?.charAt(0)?.toUpperCase() || authUser?.firstName?.charAt(0)?.toUpperCase() || authUser?.email?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: 'white',
                          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                        }}>
                          {authUser?.name || authUser?.firstName || authUser?.displayName || 'User'}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: '#9ca3af',
                          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                        }}>
                          {authUser?.email || 'user@example.com'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Menu Items */}
                  <div style={{ padding: '8px' }}>
                    {/* Home Page */}
                    <button
                      onClick={handleHomeClick}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        width: '100%',
                        padding: '12px 16px',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                        fontSize: '14px',
                        textAlign: 'left'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <Home size={16} />
                      <span>Chat Page</span>
                    </button>

                    <button
                      onClick={handleAdminClick}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        width: '100%',
                        padding: '12px 16px',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                        fontSize: '14px',
                        textAlign: 'left'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <Shield size={16} />
                      <span>Admin</span>
                      <div style={{
                        marginLeft: 'auto',
                        fontSize: '10px',
                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontWeight: '600'
                      }}>
                        ADMIN
                      </div>
                    </button>

                    {/* User Management */}
                    <button
                      onClick={handleUserListClick}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        width: '100%',
                        padding: '12px 16px',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                        fontSize: '14px',
                        textAlign: 'left'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <Shield size={16} />
                      <span>User Management</span>
                      <div style={{
                        marginLeft: 'auto',
                        fontSize: '10px',
                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontWeight: '600'
                      }}>
                        ADMIN
                      </div>
                    </button>

                    {/* Profile Page */}
                    <button
                      onClick={handleProfileClick}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        width: '100%',
                        padding: '12px 16px',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                        fontSize: '14px',
                        textAlign: 'left'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <User size={16} />
                      <span>Profile Page</span>
                    </button>

                    {/* Settings Page */}
                    <button
                      onClick={handleSettingsClick}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        width: '100%',
                        padding: '12px 16px',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                        fontSize: '14px',
                        textAlign: 'left'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <Settings size={16} />
                      <span>Settings</span>
                    </button>

                    {/* Current Page Indicator */}
                    <button
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        width: '100%',
                        padding: '12px 16px',
                        background: 'rgba(59, 130, 246, 0.1)',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        cursor: 'default',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                        fontSize: '14px',
                        textAlign: 'left',
                        opacity: 0.8
                      }}
                    >
                      <BarChart3 size={16} />
                      <span>Analytics Dashboard</span>
                      <div style={{
                        marginLeft: 'auto',
                        fontSize: '10px',
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontWeight: '600'
                      }}>
                        PRO
                      </div>
                    </button>

                    {/* Divider */}
                    <div style={{
                      height: '1px',
                      background: 'rgba(59, 130, 246, 0.2)',
                      margin: '8px 16px'
                    }} />

                    {/* Log Out */}
                    <button
                      onClick={handleLogout}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        width: '100%',
                        padding: '12px 16px',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#ef4444',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                        fontSize: '14px',
                        textAlign: 'left'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <LogOut size={16} />
                      <span>Log Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div style={{ 
        padding: '32px',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        {/* Page Header */}
        <div style={{ marginBottom: '40px' }}>
          <h1 style={{ 
            fontSize: '42px', 
            fontWeight: '700', 
            margin: '0 0 8px 0',
            background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Analytics Dashboard
          </h1>
          <p style={{ 
            fontSize: '18px', 
            color: 'rgba(255, 255, 255, 0.7)',
            margin: '0 0 16px 0'
          }}>
            Real-time insights and performance metrics for Oakland AI · As of {todayLabel}
          </p>
        </div>

        {/* Date Range Selector */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '20px',
          padding: '24px 32px',
          marginBottom: '40px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'relative'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Calendar size={24} color="white" />
            </div>
            <div>
              <h3 style={{ 
                fontSize: '20px', 
                fontWeight: '600', 
                margin: '0 0 4px 0',
                color: 'white'
              }}>
                Date Range
              </h3>
              <p style={{ 
                fontSize: '14px', 
                color: 'rgba(255, 255, 255, 0.6)',
                margin: 0
              }}>
                Currently showing: <span style={{ color: 'white', fontWeight: '600' }}>{getDisplayText()}</span>
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Date Range Dropdown */}
            <div ref={dateRangeDropdownRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setShowDateRangeDropdown(!showDateRangeDropdown)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  padding: '12px 20px',
                  background: showDateRangeDropdown 
                    ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(147, 197, 253, 0.1) 100%)'
                    : 'rgba(30, 41, 59, 0.8)',
                  border: `1px solid ${showDateRangeDropdown 
                    ? 'rgba(59, 130, 246, 0.4)'
                    : 'rgba(59, 130, 246, 0.2)'
                  }`,
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  minWidth: '200px',
                  transition: 'all 0.3s ease',
                  backdropFilter: 'blur(10px)',
                  boxShadow: showDateRangeDropdown 
                    ? '0 8px 32px rgba(59, 130, 246, 0.2)'
                    : '0 4px 16px rgba(0, 0, 0, 0.1)'
                }}
                onMouseEnter={(e) => {
                  if (!showDateRangeDropdown) {
                    e.target.style.background = 'rgba(59, 130, 246, 0.15)';
                    e.target.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!showDateRangeDropdown) {
                    e.target.style.background = 'rgba(30, 41, 59, 0.8)';
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
              >
                <span>{getDisplayText()}</span>
                <ChevronDown 
                  size={16} 
                  style={{
                    transform: showDateRangeDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease'
                  }}
                />
              </button>

              {/* Custom Dropdown Menu */}
              {showDateRangeDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '8px',
                  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.95) 100%)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '16px',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 32px rgba(59, 130, 246, 0.1)',
                  backdropFilter: 'blur(24px)',
                  zIndex: 9999,
                  animation: 'slideInDown 0.3s ease',
                  overflow: 'hidden',
                  transform: 'translateZ(0)',
                  willChange: 'transform, opacity'
                }}>
                  {[
                    { value: '7d', label: 'Last 7 days', icon: '📅' },
                    { value: '30d', label: 'Last 30 days', icon: '📊' },
                    { value: '90d', label: 'Last 90 days', icon: '📈' },
                    { value: 'custom', label: 'Custom Date Range', icon: '🗓️' }
                  ].map((option, index) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        if (option.value === 'custom') {
                          setShowDatePicker(true);
                        } else {
                          setTimeRange(option.value);
                          setCustomDateRange({ startDate: '', endDate: '' });
                          console.log(`✅ Switching to ${option.value}, fetching real data...`);
                          // The fetchAnalyticsData useEffect will automatically trigger when timeRange changes
                        }
                        setShowDateRangeDropdown(false);
                      }}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '14px 20px',
                        background: timeRange === option.value 
                          ? 'rgba(59, 130, 246, 0.2)'
                          : 'transparent',
                        border: 'none',
                        color: timeRange === option.value ? '#93c5fd' : 'white',
                        fontSize: '14px',
                        fontWeight: timeRange === option.value ? '600' : '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        textAlign: 'left',
                        borderBottom: index < 3 ? '1px solid rgba(59, 130, 246, 0.1)' : 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (timeRange !== option.value) {
                          e.target.style.background = 'rgba(59, 130, 246, 0.1)';
                          e.target.style.transform = 'translateX(4px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (timeRange !== option.value) {
                          e.target.style.background = 'transparent';
                          e.target.style.transform = 'translateX(0)';
                        }
                      }}
                    >
                      <span style={{ fontSize: '16px' }}>{option.icon}</span>
                      <span>{option.label}</span>
                      {timeRange === option.value && (
                        <span style={{ marginLeft: 'auto', color: '#3B82F6' }}>✓</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Refresh Button */}
            <button
              onClick={fetchAnalyticsData}
              disabled={refreshing}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                background: refreshing 
                  ? 'rgba(59, 130, 246, 0.3)'
                  : 'rgba(59, 130, 246, 0.2)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '12px',
                color: '#3B82F6',
                cursor: refreshing ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!refreshing) {
                  e.target.style.background = 'rgba(59, 130, 246, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                if (!refreshing) {
                  e.target.style.background = 'rgba(59, 130, 246, 0.2)';
                }
              }}
            >
              <RefreshCw size={16} style={{
                animation: refreshing ? 'spin 1s linear infinite' : 'none'
              }} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          {/* Custom Date Picker Modal */}
          {showDatePicker && (
            <>
              {/* Backdrop */}
              <div 
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.5)',
                  zIndex: 9998,
                  animation: 'fadeIn 0.2s ease-out'
                }}
                onClick={() => setShowDatePicker(false)}
              />
              
              {/* Modal */}
              <div data-datepicker style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.95) 100%)',
              border: '1px solid rgba(59, 130, 246, 0.4)',
              borderRadius: '20px',
              boxShadow: '0 25px 80px rgba(0, 0, 0, 0.6), 0 10px 40px rgba(59, 130, 246, 0.2)',
              backdropFilter: 'blur(24px)',
              minWidth: '450px',
              maxWidth: '90vw',
              zIndex: 9999,
              padding: '32px',
              animation: 'fadeInScale 0.3s ease-out',
              willChange: 'transform, opacity'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '32px',
                paddingBottom: '20px',
                borderBottom: '1px solid rgba(59, 130, 246, 0.2)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(147, 197, 253, 0.1) 100%)',
                    borderRadius: '12px',
                    border: '1px solid rgba(59, 130, 246, 0.3)'
                  }}>
                    <Calendar size={24} color="#3B82F6" />
                  </div>
                  <div>
                    <h4 style={{ 
                      fontSize: '20px',
                      fontWeight: '700',
                      color: 'white',
                      margin: '0 0 6px 0',
                      letterSpacing: '-0.5px'
                    }}>
                      Custom Date Range
                    </h4>
                    <p style={{ 
                      fontSize: '14px',
                      color: 'rgba(255, 255, 255, 0.7)',
                      margin: 0,
                      fontWeight: '400'
                    }}>
                      Select your preferred date range for detailed analytics
                    </p>
                  </div>
                </div>
                
                {/* Close Button */}
                <button
                  onClick={() => setShowDatePicker(false)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    color: 'rgba(255, 255, 255, 0.8)',
                    cursor: 'pointer',
                    padding: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    fontSize: '16px',
                    fontWeight: '500',
                    width: '44px',
                    height: '44px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(239, 68, 68, 0.2)';
                    e.target.style.borderColor = 'rgba(239, 68, 68, 0.4)';
                    e.target.style.color = '#EF4444';
                    e.target.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                    e.target.style.color = 'rgba(255, 255, 255, 0.8)';
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  ✕
                </button>
              </div>
              
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '24px',
                marginBottom: '28px'
              }}>
                <div ref={startCalendarRef} style={{ position: 'relative' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '13px',
                    color: 'rgba(255, 255, 255, 0.9)',
                    marginBottom: '10px',
                    fontWeight: '600',
                    letterSpacing: '0.5px'
                  }}>
                    📅 Start Date
                  </label>
                  <div style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <input
                      type="text"
                      placeholder="YYYY-MM-DD"
                      value={customDateRange.startDate}
                      onChange={(e) => {
                        // Allow only numbers, dashes, and valid date format
                        const value = e.target.value.replace(/[^\d-]/g, '');
                        if (value.match(/^\d{0,4}-?\d{0,2}-?\d{0,2}$/)) {
                          let formattedValue = value;
                          // Auto-format as user types
                          if (value.length >= 4 && !value.includes('-')) {
                            formattedValue = value.slice(0, 4) + '-' + value.slice(4);
                          }
                          if (value.length >= 7 && value.indexOf('-', 5) === -1) {
                            const parts = formattedValue.split('-');
                            if (parts.length === 2) {
                              formattedValue = parts[0] + '-' + parts[1].slice(0, 2) + '-' + parts[1].slice(2);
                            }
                          }
                          handleDateRangeChange('startDate', formattedValue);
                        }
                      }}
                      onFocus={() => setShowStartCalendar(true)}
                      style={{
                        width: '100%',
                        padding: '16px 50px 16px 18px',
                        background: showStartCalendar 
                          ? 'rgba(59, 130, 246, 0.1)'
                          : 'rgba(30, 41, 59, 0.8)',
                        border: `1px solid ${showStartCalendar 
                          ? 'rgba(59, 130, 246, 0.6)'
                          : 'rgba(59, 130, 246, 0.3)'
                        }`,
                        borderRadius: '14px',
                        color: 'white',
                        fontSize: '15px',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                        transition: 'all 0.3s ease',
                        backdropFilter: 'blur(10px)',
                        letterSpacing: '0.5px',
                        cursor: 'pointer',
                        boxShadow: showStartCalendar ? '0 0 20px rgba(59, 130, 246, 0.2)' : 'none'
                      }}
                    />
                    <button
                      onClick={() => setShowStartCalendar(!showStartCalendar)}
                      style={{
                        position: 'absolute',
                        right: '18px',
                        background: 'transparent',
                        border: 'none',
                        color: showStartCalendar ? '#3B82F6' : 'rgba(59, 130, 246, 0.7)',
                        fontSize: '18px',
                        cursor: 'pointer',
                        padding: '4px',
                        borderRadius: '4px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(59, 130, 246, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'transparent';
                      }}
                    >
                      📅
                    </button>
                  </div>
                  
                  {/* Calendar popup */}
                  {showStartCalendar && (
                    <DatePicker
                      selectedDate={customDateRange.startDate}
                      onDateSelect={(date) => handleDateRangeChange('startDate', date)}
                      onClose={() => setShowStartCalendar(false)}
                    />
                  )}
                </div>
                <div ref={endCalendarRef} style={{ position: 'relative' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '13px',
                    color: 'rgba(255, 255, 255, 0.9)',
                    marginBottom: '10px',
                    fontWeight: '600',
                    letterSpacing: '0.5px'
                  }}>
                    📅 End Date
                  </label>
                  <div style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <input
                      type="text"
                      placeholder="YYYY-MM-DD"
                      value={customDateRange.endDate}
                      onChange={(e) => {
                        // Allow only numbers, dashes, and valid date format
                        const value = e.target.value.replace(/[^\d-]/g, '');
                        if (value.match(/^\d{0,4}-?\d{0,2}-?\d{0,2}$/)) {
                          let formattedValue = value;
                          // Auto-format as user types
                          if (value.length >= 4 && !value.includes('-')) {
                            formattedValue = value.slice(0, 4) + '-' + value.slice(4);
                          }
                          if (value.length >= 7 && value.indexOf('-', 5) === -1) {
                            const parts = formattedValue.split('-');
                            if (parts.length === 2) {
                              formattedValue = parts[0] + '-' + parts[1].slice(0, 2) + '-' + parts[1].slice(2);
                            }
                          }
                          handleDateRangeChange('endDate', formattedValue);
                        }
                      }}
                      onFocus={() => setShowEndCalendar(true)}
                      style={{
                        width: '100%',
                        padding: '16px 50px 16px 18px',
                        background: showEndCalendar 
                          ? 'rgba(59, 130, 246, 0.1)'
                          : 'rgba(30, 41, 59, 0.8)',
                        border: `1px solid ${showEndCalendar 
                          ? 'rgba(59, 130, 246, 0.6)'
                          : 'rgba(59, 130, 246, 0.3)'
                        }`,
                        borderRadius: '14px',
                        color: 'white',
                        fontSize: '15px',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                        transition: 'all 0.3s ease',
                        backdropFilter: 'blur(10px)',
                        letterSpacing: '0.5px',
                        cursor: 'pointer',
                        boxShadow: showEndCalendar ? '0 0 20px rgba(59, 130, 246, 0.2)' : 'none'
                      }}
                    />
                    <button
                      onClick={() => setShowEndCalendar(!showEndCalendar)}
                      style={{
                        position: 'absolute',
                        right: '18px',
                        background: 'transparent',
                        border: 'none',
                        color: showEndCalendar ? '#3B82F6' : 'rgba(59, 130, 246, 0.7)',
                        fontSize: '18px',
                        cursor: 'pointer',
                        padding: '4px',
                        borderRadius: '4px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(59, 130, 246, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'transparent';
                      }}
                    >
                      📅
                    </button>
                  </div>
                  
                  {/* Calendar popup */}
                  {showEndCalendar && (
                    <DatePicker
                      selectedDate={customDateRange.endDate}
                      onDateSelect={(date) => handleDateRangeChange('endDate', date)}
                      onClose={() => setShowEndCalendar(false)}
                    />
                  )}
                </div>
              </div>
              
              {/* Date Format Helper */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '20px',
                padding: '12px 16px',
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '12px'
              }}>
                <span style={{ fontSize: '16px' }}>💡</span>
                <span style={{
                  fontSize: '13px',
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontWeight: '500'
                }}>
                  Enter dates in YYYY-MM-DD format (e.g., {new Date().toISOString().slice(0, 10)})
                </span>
              </div>
              
              <div style={{ 
                display: 'flex',
                gap: '16px',
                justifyContent: 'flex-end',
                marginTop: '32px',
                paddingTop: '24px',
                borderTop: '1px solid rgba(59, 130, 246, 0.2)'
              }}>
                <button
                  onClick={resetDateRange}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '14px 24px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '12px',
                    color: 'rgba(255, 255, 255, 0.9)',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                    transition: 'all 0.3s ease',
                    backdropFilter: 'blur(10px)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(255, 255, 255, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  🔄 Reset
                </button>
                <button
                  onClick={applyCustomDateRange}
                  disabled={!customDateRange.startDate || !customDateRange.endDate}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '14px 28px',
                    background: (!customDateRange.startDate || !customDateRange.endDate)
                      ? 'rgba(59, 130, 246, 0.3)'
                      : 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    cursor: (!customDateRange.startDate || !customDateRange.endDate)
                      ? 'not-allowed'
                      : 'pointer',
                    fontSize: '14px',
                    fontWeight: '700',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                    transition: 'all 0.3s ease',
                    boxShadow: (!customDateRange.startDate || !customDateRange.endDate)
                      ? 'none'
                      : '0 8px 25px rgba(59, 130, 246, 0.4)',
                    opacity: (!customDateRange.startDate || !customDateRange.endDate) ? 0.6 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (customDateRange.startDate && customDateRange.endDate) {
                      e.target.style.background = 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)';
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 12px 35px rgba(59, 130, 246, 0.5)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (customDateRange.startDate && customDateRange.endDate) {
                      e.target.style.background = 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 8px 25px rgba(59, 130, 246, 0.4)';
                    }
                  }}
                >
                  ✅ Apply Range
                </button>
              </div>
              </div>
            </>
          )}
        </div>

        {/* Stats Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
          gap: '24px', 
          marginBottom: '40px' 
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '24px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #3B82F6, #1D4ED8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Users size={24} color="white" />
              </div>
            </div>
            <div style={{ color: 'white' }}>
              <div style={{ fontSize: '14px', opacity: 0.8, marginBottom: '8px', fontWeight: '500' }}>
                Total Users
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', marginBottom: '4px', lineHeight: '1' }}>
                {analyticsData.userStats?.totalUsers?.toLocaleString() || '0'}
              </div>
              <div style={{ fontSize: '14px', opacity: 0.7 }}>
                {getDisplayText()}
              </div>
            </div>
          </div>
          
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '24px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Brain size={24} color="white" />
              </div>
            </div>
            <div style={{ color: 'white' }}>
              <div style={{ fontSize: '14px', opacity: 0.8, marginBottom: '8px', fontWeight: '500' }}>
                Total Chats
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', marginBottom: '4px', lineHeight: '1' }}>
                {analyticsData.userStats?.totalChats?.toLocaleString() || '0'}
              </div>
              <div style={{ fontSize: '14px', opacity: 0.7 }}>
                {getDisplayText()}
              </div>
            </div>
          </div>
          
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '24px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #10B981, #059669)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <UserCheck size={24} color="white" />
              </div>
            </div>
            <div style={{ color: 'white' }}>
              <div style={{ fontSize: '14px', opacity: 0.8, marginBottom: '8px', fontWeight: '500' }}>
                Active Users
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', marginBottom: '4px', lineHeight: '1' }}>
                {analyticsData.userStats?.activeUsers?.toLocaleString() || '0'}
              </div>
              <div style={{ fontSize: '14px', opacity: 0.7 }}>
                {getDisplayText()}
              </div>
            </div>
          </div>
          
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '24px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Target size={24} color="white" />
              </div>
            </div>
            <div style={{ color: 'white' }}>
              <div style={{ fontSize: '14px', opacity: 0.8, marginBottom: '8px', fontWeight: '500' }}>
                New Users
              </div>
              <div style={{ fontSize: '32px', fontWeight: '700', marginBottom: '4px', lineHeight: '1' }}>
                {analyticsData.userStats?.newUsersThisWeek?.toLocaleString() || '0'}
              </div>
              <div style={{ fontSize: '14px', opacity: 0.7 }}>
                {getDisplayText()}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '2fr 1fr', 
          gap: '32px',
          marginBottom: '40px'
        }}>
          {/* Left Column - Charts */}
          <div>
            {/* User Growth Chart */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '24px',
              padding: '32px',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                  <h3 style={{ fontSize: '24px', fontWeight: '600', margin: '0 0 8px 0', color: 'white' }}>
                    User Growth & Engagement
                  </h3>
                  <p style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
                    {getDisplayText()} • {getChartGroupingLabel()} • {analyticsData.userGrowthData?.length || 0} data points • Currently showing: <span style={{ color: 'white', fontWeight: '600' }}>
                      {selectedMetric === 'chats' ? 'Chat Sessions' : 
                       selectedMetric === 'users' ? 'Active Users' : 
                       selectedMetric === 'messages' ? 'Messages Sent' : 'Engagement Rate'}
                    </span>
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ 
                    fontSize: '12px', 
                    color: 'rgba(255, 255, 255, 0.6)', 
                    marginRight: '8px',
                    fontWeight: '500'
                  }}>
                    Metric:
                  </span>
                  {[
                    { key: 'chats', label: 'Chats', color: '#3B82F6' },
                    { key: 'users', label: 'Users', color: '#10B981' },
                    { key: 'messages', label: 'Messages', color: '#8B5CF6' },
                    { key: 'engagement', label: 'Engagement', color: '#F59E0B' }
                  ].map((metric) => (
                    <button
                      key={metric.key}
                      onClick={() => setSelectedMetric(metric.key)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '12px',
                        background: selectedMetric === metric.key 
                          ? metric.color 
                          : 'rgba(255, 255, 255, 0.1)',
                        border: selectedMetric === metric.key 
                          ? `1px solid ${metric.color}` 
                          : '1px solid rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontSize: '14px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        fontWeight: selectedMetric === metric.key ? '600' : '500',
                        minWidth: '80px',
                        textAlign: 'center'
                      }}
                    >
                      {metric.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Chart Stats */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: '16px', 
                marginBottom: '24px' 
              }}>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  padding: '16px',
                  textAlign: 'center',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: 'white', marginBottom: '4px' }}>
                    {currentValue.toLocaleString()}
                  </div>
                  <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                    Current {selectedMetric}
                  </div>
                </div>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  padding: '16px',
                  textAlign: 'center',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <div style={{ 
                    fontSize: '18px', 
                    fontWeight: '600', 
                    color: change >= 0 ? '#10B981' : '#EF4444',
                    marginBottom: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px'
                  }}>
                    {change >= 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                    {Math.abs(change).toFixed(1)}%
                  </div>
                  <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                    vs previous
                  </div>
                </div>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  padding: '16px',
                  textAlign: 'center',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: 'white', marginBottom: '4px' }}>
                    {average.toLocaleString()}
                  </div>
                  <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                    Average
                  </div>
                </div>
              </div>
              
              <div style={{ height: '300px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={analyticsData.userGrowthData}>
                    <defs>
                      <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={selectedMetric === 'chats' ? '#3B82F6' : selectedMetric === 'users' ? '#10B981' : selectedMetric === 'messages' ? '#8B5CF6' : '#F59E0B'} stopOpacity={0.4}/>
                        <stop offset="95%" stopColor={selectedMetric === 'chats' ? '#3B82F6' : selectedMetric === 'users' ? '#10B981' : selectedMetric === 'messages' ? '#8B5CF6' : '#F59E0B'} stopOpacity={0.05}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                    <XAxis 
                      dataKey="date" 
                      stroke="rgba(255, 255, 255, 0.6)"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis 
                      stroke="rgba(255, 255, 255, 0.6)"
                      fontSize={12}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => {
                        if (selectedMetric === 'engagement') return `${value}%`;
                        return value;
                      }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area 
                      type="natural" 
                      dataKey={selectedMetric} 
                      stroke={selectedMetric === 'chats' ? '#3B82F6' : selectedMetric === 'users' ? '#10B981' : selectedMetric === 'messages' ? '#8B5CF6' : '#F59E0B'} 
                      strokeWidth={3}
                      fill="url(#chartGradient)"
                      activeDot={{ 
                        r: 6, 
                        stroke: selectedMetric === 'chats' ? '#3B82F6' : selectedMetric === 'users' ? '#10B981' : selectedMetric === 'messages' ? '#8B5CF6' : '#F59E0B', 
                        strokeWidth: 2, 
                        fill: '#fff' 
                      }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              
              {/* Chart Legend */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '24px', 
                marginTop: '16px',
                padding: '16px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: selectedMetric === 'chats' ? '#3B82F6' : selectedMetric === 'users' ? '#10B981' : selectedMetric === 'messages' ? '#8B5CF6' : '#F59E0B'
                  }} />
                  <span style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)' }}>
                    {selectedMetric === 'chats' ? 'Chat Sessions' : 
                     selectedMetric === 'users' ? 'Active Users' : 
                     selectedMetric === 'messages' ? 'Messages Sent' : 'Engagement Rate'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Last Questions Section */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '24px',
            padding: '24px',
            marginTop: '24px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <MessageCircle size={20} color="white" />
              </div>
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: '600', margin: '0 0 4px 0', color: 'white' }}>
                  Popular Quick Topics ({getDisplayText()})
                </h3>
                <p style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
                  Most used quick option topics by users
                </p>
              </div>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {analyticsData.popularQuickTopics?.length > 0 ? (
                analyticsData.popularQuickTopics.map((topic, index) => (
                <div key={index} style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '16px',
                  padding: '16px',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '12px',
                      background: index < 3 
                        ? 'linear-gradient(135deg, #F59E0B, #D97706)'
                        : 'linear-gradient(135deg, #6B7280, #4B5563)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: '700'
                    }}>
                      #{index + 1}
                    </div>
                    
                    <div style={{
                      padding: '6px 12px',
                      borderRadius: '16px',
                      background: 'rgba(59, 130, 246, 0.2)',
                      color: '#3B82F6',
                      fontSize: '12px',
                      fontWeight: '600',
                      border: '1px solid rgba(59, 130, 246, 0.3)'
                    }}>
                      {topic.category}
                    </div>
                  </div>

                  <div style={{ marginBottom: '8px' }}>
                    <div style={{ 
                      fontSize: '14px', 
                      fontWeight: '600', 
                      color: 'white', 
                      marginBottom: '6px',
                      lineHeight: '1.4'
                    }}>
                      {topic.topic}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 8px',
                      borderRadius: '10px',
                      background: 'rgba(16, 185, 129, 0.2)',
                      color: '#10B981',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      <MessageCircle size={14} />
                      {topic.count} uses
                    </div>
                  </div>
                </div>
                ))
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '14px'
                }}>
                  No quick topic data available for the selected period
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Insights */}
          <div>
            {/* Question Categories */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '24px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <BarChart3 size={20} color="white" />
                </div>
                <div>
                  <h3 style={{ fontSize: '20px', fontWeight: '600', margin: '0 0 4px 0', color: 'white' }}>
                    Question Categories
                  </h3>
                  <p style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
                    Most common question types
                  </p>
                </div>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {analyticsData.questionCategories.map((category, index) => (
                  <div key={index} style={{
                    background: 'rgba(0, 0, 0, 0.8)',
                    backdropFilter: 'blur(20px)',
                    border: `2px solid ${category.color}60`,
                    borderRadius: '16px',
                    padding: '20px',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = `0 20px 40px rgba(0, 0, 0, 0.5), 0 0 0 2px ${category.color}`;
                    e.currentTarget.style.border = `2px solid ${category.color}`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.4)';
                    e.currentTarget.style.border = `2px solid ${category.color}60`;
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          background: category.color,
                          boxShadow: `0 0 8px ${category.color}40`
                        }} />
                        <span style={{ 
                          fontSize: '16px', 
                          fontWeight: '600', 
                          color: 'white' 
                        }}>
                          {category.name}
                        </span>
                      </div>
                      
                      <div style={{
                        fontSize: '18px',
                        fontWeight: '700',
                        color: 'white',
                        background: `${category.color}20`,
                        padding: '8px 16px',
                        borderRadius: '12px',
                        border: `2px solid ${category.color}60`,
                        boxShadow: `0 4px 12px ${category.color}30`
                      }}>
                        {category.value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Business Types */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '24px',
              padding: '24px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #10B981, #059669)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Globe size={20} color="white" />
                </div>
                <div>
                  <h3 style={{ fontSize: '20px', fontWeight: '600', margin: '0 0 4px 0', color: 'white' }}>
                    Business Types
                  </h3>
                  <p style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
                    Distribution of business categories
                  </p>
                </div>
              </div>
              
              <div style={{ height: '200px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={analyticsData.businessTypeData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {analyticsData.businessTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              <div style={{ marginTop: '16px' }}>
                {analyticsData.businessTypeData.map((type, index) => (
                  <div key={index} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '8px',
                    fontSize: '14px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        background: type.color
                      }} />
                      <span style={{ color: 'rgba(255, 255, 255, 0.8)' }}>{type.name}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: 'white', fontWeight: '600' }}>{type.value}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>


        {/* Recent Activities Analytics Section */}
        <div style={{ marginTop: '32px' }}>
          <RecentActivitiesAnalytics />
        </div>
      </div>
      </div>
    </>
  );
}

export default AnalyticsPage2025Fixed; 